import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  Button,
  Chip
} from '@mui/material';
import { SketchPicker } from 'react-color';

const ColorPresets = [
  { name: 'Professional Blue', primary: '#0066cc', secondary: '#1f2937', accent: '#10b981' },
  { name: 'Medical Green', primary: '#059669', secondary: '#374151', accent: '#3b82f6' },
  { name: 'Legal Navy', primary: '#1e3a8a', secondary: '#1f2937', accent: '#d97706' },
  { name: 'Creative Purple', primary: '#7c3aed', secondary: '#1f2937', accent: '#f59e0b' },
  { name: 'Modern Teal', primary: '#0d9488', secondary: '#1e293b', accent: '#ec4899' },
  { name: 'Bold Red', primary: '#dc2626', secondary: '#1f2937', accent: '#facc15' },
  { name: 'Elegant Black', primary: '#1f2937', secondary: '#374151', accent: '#3b82f6' },
  { name: 'Warm Orange', primary: '#ea580c', secondary: '#1f2937', accent: '#06b6d4' }
];

const ColorPickerField = ({ label, color, onChange }) => {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <Box>
      <Typography variant="body2" fontWeight="medium" gutterBottom>
        {label}
      </Typography>
      <Box display="flex" gap={1} alignItems="center">
        <Box
          sx={{
            width: 60,
            height: 60,
            backgroundColor: color,
            border: '2px solid',
            borderColor: 'divider',
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.main'
            }
          }}
          onClick={() => setShowPicker(!showPicker)}
        />
        <TextField
          value={color}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          sx={{ width: 120 }}
          placeholder="#000000"
        />
      </Box>
      {showPicker && (
        <Box sx={{ position: 'absolute', zIndex: 2, mt: 1 }}>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0
            }}
            onClick={() => setShowPicker(false)}
          />
          <SketchPicker
            color={color}
            onChange={(color) => onChange(color.hex)}
          />
        </Box>
      )}
    </Box>
  );
};

const TripleColorPicker = ({ value, onChange }) => {
  const colors = value || {
    primary: '#0066cc',
    secondary: '#1f2937',
    accent: '#10b981'
  };

  const handleColorChange = (colorType, newColor) => {
    onChange({
      ...colors,
      [colorType]: newColor
    });
  };

  const applyPreset = (preset) => {
    onChange({
      primary: preset.primary,
      secondary: preset.secondary,
      accent: preset.accent
    });
  };

  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Choose Your Color Scheme
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select or customize your brand colors
      </Typography>

      {/* Color Presets */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" fontWeight="medium" gutterBottom>
          Quick Presets
        </Typography>
        <Grid container spacing={1}>
          {ColorPresets.map((preset, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 2
                  }
                }}
                onClick={() => applyPreset(preset)}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" gap={0.5} mb={1}>
                    <Box
                      sx={{
                        flex: 1,
                        height: 30,
                        backgroundColor: preset.primary,
                        borderRadius: 0.5
                      }}
                    />
                    <Box
                      sx={{
                        flex: 1,
                        height: 30,
                        backgroundColor: preset.secondary,
                        borderRadius: 0.5
                      }}
                    />
                    <Box
                      sx={{
                        flex: 1,
                        height: 30,
                        backgroundColor: preset.accent,
                        borderRadius: 0.5
                      }}
                    />
                  </Box>
                  <Typography variant="caption" fontWeight="medium">
                    {preset.name}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Custom Color Pickers */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight="medium" gutterBottom>
          Custom Colors
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <ColorPickerField
              label="Primary Color"
              color={colors.primary}
              onChange={(color) => handleColorChange('primary', color)}
            />
            <Typography variant="caption" color="text.secondary">
              Main brand color (buttons, links)
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <ColorPickerField
              label="Secondary Color"
              color={colors.secondary}
              onChange={(color) => handleColorChange('secondary', color)}
            />
            <Typography variant="caption" color="text.secondary">
              Headers, backgrounds
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <ColorPickerField
              label="Accent Color"
              color={colors.accent}
              onChange={(color) => handleColorChange('accent', color)}
            />
            <Typography variant="caption" color="text.secondary">
              Highlights, CTAs
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Preview */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            Color Preview
          </Typography>
          <Box
            sx={{
              height: 120,
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex'
            }}
          >
            <Box
              sx={{
                flex: 2,
                backgroundColor: colors.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}
            >
              Primary
            </Box>
            <Box
              sx={{
                flex: 2,
                backgroundColor: colors.secondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}
            >
              Secondary
            </Box>
            <Box
              sx={{
                flex: 1,
                backgroundColor: colors.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}
            >
              Accent
            </Box>
          </Box>
          <Box display="flex" gap={1} mt={2}>
            <Chip label={`Primary: ${colors.primary}`} size="small" />
            <Chip label={`Secondary: ${colors.secondary}`} size="small" />
            <Chip label={`Accent: ${colors.accent}`} size="small" />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TripleColorPicker;
